import { Fragment } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { isXBaselineAccessor, isYBaselineAccessor } from './BarDisplay.js';
import { overlayContainer } from './BarLabelOverlay.sty.js';
import { HorizontalBarLabel } from './HorizontalBarLabel.js';
import { VerticalBarLabel } from './VerticalBarLabel.js';
import { getCanvasColor } from '../../../../core/components/canvas/utils/canvas-color.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { getColorByContrast } from '../../../../core/utils/color-contrast.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import { XYAxisTypeCategorical } from '../../../types/axis.js';
import type {
  BarMetadata,
  BarSeriesWithDatapoints,
} from '../../../types/xy-chart-internals.js';
import { type BarDatapoint } from '../../../types/xy-chart.js';
import {
  getCurrentScale,
  getDimensionScale,
} from '../../../utils/get-scale.js';
import { relAccessor } from '../../../utils/value-representation.js';
import {
  heightAccessor,
  widthAccessor,
  xAccessor,
  yAccessor,
} from '../bar-accessors.js';

interface BarLabelOverlayProps {
  axisIdToScales: AxisIdToScales;
  series: BarSeriesWithDatapoints;
  metadata: BarMetadata;
  opacity?: number;
}

function filterToScaleDomain(
  data: BarDatapoint[],
  scale: unknown,
  isVertical: boolean,
): BarDatapoint[] {
  const domainFn = (scale as { domain?: () => unknown[] } | undefined)?.domain;
  if (typeof domainFn !== 'function') {
    return data;
  }
  const domain = domainFn.call(scale);
  if (!Array.isArray(domain)) {
    return data;
  }
  const allowed = new Set(domain as string[]);
  return data.filter((d) => {
    const key = isVertical ? d.x0 : d.y0;
    return typeof key === 'string' ? allowed.has(key) : true;
  });
}

/**
 * Renders the HTML bar labels for a single bar series. Each datapoint produces
 * a `HorizontalBarLabel` or `VerticalBarLabel` depending on the chart's
 * categorical-axis orientation; both use the split-clip pattern to paint the
 * portion inside the bar with a contrasting color and the portion overflowing
 * to the chart edge with the neutral text color.
 *
 * Rendered as a sibling of the `<Canvas>` element (not inside `BarDisplay`)
 * because HTML children of `<canvas>` are fallback content, not visible DOM.
 * See `BarLabelLayout.tsx` for the chart-level wrapper that iterates over
 * labeled bar series and mounts this component.
 */
export function BarLabelOverlay({
  axisIdToScales,
  series,
  metadata,
  opacity,
}: Readonly<BarLabelOverlayProps>) {
  const { width: canvasWidth, height: canvasHeight } = useGridPlotSize();
  const { datapointMetadataMap, axisIdToScaleDomain } = useXYChartData();
  const { seriesToMetadata } = useBarData();
  const { truncationMode } = useXYChartTruncationMode();

  if (!series.hasLabels) {
    return null;
  }

  // Resolve scales the same way BarDisplay does — for the categorical axis
  // side (the one with a baseline accessor) we pass series.type so the bar's
  // categoricalBarScale is preferred over currentScale. Without this the label
  // can land on a different sub-band than the bar it belongs to.
  const xScale = getCurrentScale(
    axisIdToScales,
    series.xAxisId,
    isXBaselineAccessor(series.data[0]) ? series.type : undefined,
  );
  const dimensionXScale = getDimensionScale(axisIdToScales, series.xAxisId);
  const yScale = getCurrentScale(
    axisIdToScales,
    series.yAxisId,
    isYBaselineAccessor(series.data[0]) ? series.type : undefined,
  );
  const dimensionYScale = getDimensionScale(axisIdToScales, series.yAxisId);

  const { xAxis, yAxis } = safeGet(seriesToMetadata, series);
  const isVertical = xAxis.type === XYAxisTypeCategorical;

  const isRTL = !isVertical && Boolean(xAxis.reversed);
  const isTTB = isVertical && Boolean(yAxis.reversed);

  const xFn = xAccessor(
    xScale,
    datapointMetadataMap,
    dimensionXScale,
    series.id,
    relAccessor(xAxis, axisIdToScaleDomain),
  );
  const yFn = yAccessor(
    yScale,
    datapointMetadataMap,
    dimensionYScale,
    series.id,
    relAccessor(yAxis, axisIdToScaleDomain),
  );
  const wFn = widthAccessor(
    xScale,
    datapointMetadataMap,
    dimensionXScale,
    relAccessor(xAxis, axisIdToScaleDomain),
  );
  const hFn = heightAccessor(
    yScale,
    datapointMetadataMap,
    dimensionYScale,
    relAccessor(yAxis, axisIdToScaleDomain),
  );

  const fillHex = getCanvasColor(metadata.color.Default);
  // Inside the bar: contrast against the bar fill — uses theme-aware on-accent
  // tokens so the chosen color matches the active theme's palette.
  const insideColor = getColorByContrast(fillHex);
  // Outside the bar: contrast against the chart background — theme-aware via
  // the design token CSS variable.
  const outsideColor = Colors.Text.Neutral.Default;

  const data = filterToScaleDomain(
    series.data,
    isVertical ? xScale : yScale,
    isVertical,
  );

  const dimStyle = opacity !== undefined ? { opacity } : undefined;

  return (
    <div className={overlayContainer} style={dimStyle}>
      {data.map((datum, index) => {
        const text = datum.label;
        if (!text) {
          return null;
        }

        const x = xFn(datum);
        const y = yFn(datum);
        const w = wFn(datum);
        const h = hFn(datum);
        const barWidth = Math.abs(w);
        const barHeight = Math.abs(h);

        return (
          <Fragment key={index}>
            {isVertical ? (
              <VerticalBarLabel
                x={x}
                y={y}
                barWidth={barWidth}
                barHeight={barHeight}
                canvasHeight={canvasHeight}
                isReversed={isTTB}
                text={text}
                insideColor={insideColor}
                outsideColor={outsideColor}
                truncationMode={truncationMode}
              />
            ) : (
              <HorizontalBarLabel
                x={x}
                y={y}
                barWidth={barWidth}
                barHeight={barHeight}
                canvasWidth={canvasWidth}
                isReversed={isRTL}
                text={text}
                insideColor={insideColor}
                outsideColor={outsideColor}
                truncationMode={truncationMode}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

BarLabelOverlay['displayName'] = 'BarLabelOverlay';
